import { COLORS } from "assets";
import {
  BoxStatuses,
  CameraStatuses,
  CCA2,
  CCA3,
  CountryCCA3,
  LabelValueType,
  MemberRole,
  ModelLabels,
  ModelTypes,
  ThreatStatusTypes,
  TimezoneTypes,
  TunnelStatuses,
  UserType,
  VirtuoseRTSPMethods
} from "types";
import countries from "./countriesV3.1.json";

/** Max time toast notifications stay visible (ms). */
export const SNACKBAR_AUTO_HIDE_MS = 4000;

export const MSG_CODE = {
  FEATURE_DISABLED: "FEATURE_DISABLED",
  UNAUTHENTICATED: "UNAUTHENTICATED",
  TOKEN_MISMATCH: "TOKEN_MISMATCH",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  UNAUTHORIZED: "UNAUTHORIZED",
  BREAK_GLASS_REQUIRED: "BREAK_GLASS_REQUIRED",
  REQUEST_CANCELED: "Request Canceled",
  COMPONENT_UNMOUNT: "Component Unmount"
};

export const BREAK_GLASS_EVENT = "neuravue:break-glass-required";
export const BREAK_GLASS_APPROVED_EVENT = "neuravue:break-glass-approved";
export const baseURL = process.env.NODE_ENV === "development" ? "http://localhost:3002" : "";
export const frontEndURL = process.env.NODE_ENV === "development" ? "http://localhost:3003" : baseURL;
export const apiURL = baseURL + "/api/v1";
export const ENDPOINTS = {
  account: apiURL + "/account",
  forget: apiURL + "/account/forget",
  google: apiURL + "/account/google",
  apple: apiURL + "/account/apple",
  otp: apiURL + "/account/otp",
  users: apiURL + "/user",
  sites: apiURL + "/site",
  APIToken: apiURL + "/api-token",
  boxes: apiURL + "/box",
  boxLogs: apiURL + "/box-logs",
  cameras: apiURL + "/camera",
  getFrame: apiURL + "/camera/get-frame/",
  members: apiURL + "/member",
  memberCandidates: apiURL + "/member/candidates",
  threats: apiURL + "/threat",
  threatsSiteWise: apiURL + "/threat/site-wise",
  signed: apiURL + "/signed",
  breakGlass: apiURL + "/break-glass",
  subscription: apiURL + "/subscription",
  auth: apiURL + "/camera/auth",
  token: apiURL + "/camera/token"
};
export const USER_TYPES: UserType[] = ["user", "admin", "super"];
export const MEMBER_ROLES: LabelValueType<MemberRole>[] = [
  {
    label: "Admin",
    value: "admin"
  },
  {
    label: "User",
    value: "user"
  }
];
/** Portal site/camera/dashboard toggles. */
export const UI_MODELS: ModelTypes[] = ["zone", "gesture", "person", "fight", "weapon", "abusive"];

export const MODELS: ModelTypes[] = [
  "zone", // 0
  "gesture", // 1
  "person", // 2
  "fight", // 3
  "weapon", // 4
  "abusive" // 5
];
export const AI_MODELS: { [key in ModelTypes]: ModelTypes } = {
  zone: "zone",
  gesture: "gesture",
  person: "person",
  fight: "fight",
  weapon: "weapon",
  abusive: "abusive"
};
/** Display copy only for abusive. Edge model ids stay "fight" and "abusive". */
export const ABUSE_DETECTION_LABEL = "Responsive Behaviour detection" as const;
/** Short name for abusive alert rows, charts, notifications, and exports. */
export const ABUSE_DETECTION_ALERT_LABEL = "Responsive Behaviour detection" as const;

export const AI_MODEL_LABELS: { [key in ModelTypes]: string } = {
  zone: "Wandering Detection",
  gesture: "Distress recognition",
  person: "Fall detection",
  fight: "Fight detection",
  weapon: "Weapon Detection",
  abusive: ABUSE_DETECTION_LABEL
};
export const MODEL_LABELS: ModelLabels[] = [
  "Wandering Detection", // 0
  "Distress recognition", // 1
  "Fall detection", // 2
  "Fight detection", // 3 fight
  "Weapon Detection", // 4
  ABUSE_DETECTION_LABEL // 5 abusive
];

/** i18n key for an alert/event type. Does not change the model id the edge sends. */
export function alertTypeLabelKey(model?: string): string {
  if (model === "abusive") return ABUSE_DETECTION_ALERT_LABEL;
  if (model && Object.prototype.hasOwnProperty.call(AI_MODEL_LABELS, model)) {
    return AI_MODEL_LABELS[model as ModelTypes];
  }
  return model || "";
}

export function alertTypeLabelKeyAt(index: number): string {
  return alertTypeLabelKey(MODELS[index]);
}

export function filterModelsForUI(models?: ModelTypes[]): ModelTypes[] {
  const source = Array.isArray(models) && models.length ? models : [...UI_MODELS];
  return source.filter((m) => UI_MODELS.includes(m));
}

export function modelSupportsAudio(model: ModelTypes): boolean {
  return model === "abusive";
}

export function normalizeActiveModelMedia(model: ModelTypes, video: boolean, audio: boolean) {
  if (!modelSupportsAudio(model)) {
    if (!video) return { video: false, audio: false };
    return { video: true, audio: false };
  }
  if (!video && !audio) return { video: false, audio: false };
  return { video, audio };
}

type ColorKeys = keyof typeof COLORS;
export const MODEL_COLORS: ColorKeys[] = [
  "success", // 0
  "error", // 1
  "warning", // 2
  "info", // 3
  "primary", // 4
  "error" // 5 abusive
];
/** Figma Dashboard chart palette (Zone → Abusive) */
export const MODEL_CHART_COLORS: string[] = [
  COLORS.chart.zone,
  COLORS.chart.distress,
  COLORS.chart.fall,
  COLORS.chart.abuse,
  COLORS.chart.weapon,
  COLORS.chart.abusive
];
export const LANGUAGES: string[] = ["English", "Français"];
export const THREAT_STATUSES: ThreatStatusTypes[] = ["Action Required", "Resolved", "Reported"];
export const THREAT_STATUS_COLORS: { [key in ThreatStatusTypes]: string } = {
  "Action Required": "error",
  Resolved: "success",
  Reported: "info"
};
export const PASSWORD_RULES =
  "Password must be minimum 8 characters long and include 1 digit, 1 uppercase, 1 lowercase and 1 special character.";
export const drawerWidth = 260;
export const placeholderImage = "/assets/img/placeholder-image.png";
export const personPlaceholderImage = "/assets/img/person-placeholder-image.png";

export const BOX_STATUS_COLORS: { [key in BoxStatuses]: ColorKeys } = {
  off: "secondary",
  running: "success",
  loading: "info",
  failed: "error"
};
export const BOX_STATUS_LABELS: { [key in BoxStatuses]: string } = {
  off: "Device OFF",
  running: "Running",
  loading: "Loading...",
  failed: "Failed"
};

export const TUNNEL_STATUS_LABELS: { [key in TunnelStatuses]: string } = {
  unknown: "—",
  healthy: "Tunnel Healthy",
  down: "Tunnel Down"
};

export const CAMERA_STATUS_COLORS: { [key in CameraStatuses]: ColorKeys } = {
  disconnected: "secondary",
  connected: "success",
  invalid: "error"
};
export const CAMERA_STATUS_LABELS: { [key in CameraStatuses]: string } = {
  disconnected: "Disconnected",
  connected: "Connected",
  invalid: "RTSP Error"
};
export const VIRTUOSE_RTSP_METHODS: VirtuoseRTSPMethods[] = ["cameraIpAndProfile", "cameraIpNameAndIndex"];
export const VIRTUOSE_RTSP_METHOD_LABELS: { [key in VirtuoseRTSPMethods]: string } = {
  cameraIpAndProfile: "Camera IP And Profile",
  cameraIpNameAndIndex: "Camera IP, Name And Index"
};
export const TIMEZONES = (() => {
  const TIMEZONES: {
    label: string;
    value: string;
    timezone: TimezoneTypes;
  }[] = [];
  countries.forEach((item) =>
    item.timezones.forEach((timezone) => {
      const val = timezone === "UTC" ? "UTC+00:00" : timezone;
      const label = `(${val}) ${item.name.common}`;
      TIMEZONES.push({ label, value: label, timezone: val as TimezoneTypes });
    })
  );
  return TIMEZONES.sort((a, b) => a.value.localeCompare(b.value));
})();
export const COUNTRY_CCA3 = (() =>
  countries
    .map(
      (item) =>
        ({
          value: item.cca3 as CCA3,
          label: item.name.common,
          cca2: item.cca2 as CCA2
        } as CountryCCA3)
    )
    .sort((a, b) => a.label.localeCompare(b.label)))();
